import type { HoComponents } from '@pos/api-client';

/**
 * The price table being edited (FR-MENU-06): rows as loaded, plus what the user added, changed or removed. Nothing
 * reaches the server until it is saved, all at once.
 */
export type Kind = 'item' | 'deal';
export const KINDS: readonly Kind[] = ['item', 'deal'];

type TableDto = HoComponents['schemas']['PriceTableDto'];
export type Candidate = HoComponents['schemas']['PriceCandidateDto'];
export type PriceUnit = HoComponents['schemas']['PriceUnitDto'];

/** One thing to price: an item in one of its units, or a deal (no unit). */
export type PriceTarget = Pick<Candidate, 'id' | 'code' | 'name' | 'groupName' | 'isActive'> & {
  unit?: Pick<PriceUnit, 'uomId' | 'code' | 'name'>;
  /** All the item's selling units, to change the unit a row prices. */
  units?: readonly PriceUnit[];
};

/** What identifies a priced row inside a table: the target and its unit (ADR 0030). */
export const unitKey = (targetId: string, uomId?: string | null) => `${targetId}|${uomId ?? ''}`;

export interface PriceRow {
  key: string;
  entryId?: string;
  version?: number;
  kind: Kind;
  targetId: string;
  /** The unit an item is priced in; none for a deal. */
  uomId?: string | null;
  uomCode?: string | null;
  uomName?: string | null;
  /** An item's selling units, to pick the row's unit from. */
  units: readonly PriceUnit[];
  /** The item's or deal's VAT category on this price list, the same on all its rows; null = its own (ADR 0031, ADR 0033). */
  taxCategoryId: string | null;
  /** The item's own VAT category, or the deal's (none: each of its items' own), when known. */
  itemTaxCategoryId?: string | null;
  code: string;
  name: string;
  groupName?: string | null;
  isActive: boolean;
  /** As typed; a number once valid. */
  price: string;
  /** Local days, yyyy-mm-dd; `to` may be empty. */
  from: string;
  to: string;
  removed: boolean;
  original?: { price: string; from: string; to: string; uomId: string | null; taxCategoryId: string | null };
}

export type Problem = 'price' | 'from' | 'dates' | 'duplicate';

let counter = 0;
const nextKey = () => `new-${++counter}`;

/** A timestamp as the local day it falls on. */
export function toDay(iso: string | null | undefined) {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** The start of a local day as an instant. */
export const fromDay = (day: string) => new Date(`${day}T00:00:00`).toISOString();

export const today = () => toDay(new Date().toISOString());

export function fromTable(table: TableDto): PriceRow[] {
  return table.rows.map((r) => {
    const values = { price: String(Number(r.price)), from: toDay(r.effectiveFrom), to: toDay(r.effectiveTo) };
    const uomId = r.uomId ?? null;
    const taxCategoryId = r.taxCategoryId ?? null;
    return {
      key: r.entryId,
      entryId: r.entryId,
      version: Number(r.version),
      kind: r.kind as Kind,
      targetId: r.targetId,
      uomId,
      uomCode: r.uomCode ?? null,
      uomName: r.uomName ?? null,
      units: r.units ?? [],
      taxCategoryId,
      itemTaxCategoryId: r.itemTaxCategoryId ?? null,
      code: r.code,
      name: r.name,
      groupName: r.groupName,
      isActive: r.isActive,
      ...values,
      removed: false,
      original: { ...values, uomId, taxCategoryId },
    };
  });
}

export const isNew = (row: PriceRow) => !row.entryId;

export const isChanged = (row: PriceRow) =>
  Boolean(row.original) &&
  !row.removed &&
  (row.price !== row.original?.price ||
    row.from !== row.original?.from ||
    row.to !== row.original?.to ||
    (row.uomId ?? null) !== row.original?.uomId ||
    row.taxCategoryId !== row.original?.taxCategoryId);

/** Rows that will be sent: new, changed or removed. */
export const changes = (rows: readonly PriceRow[]) =>
  rows.filter((r) => (isNew(r) && !r.removed) || isChanged(r) || (r.removed && r.entryId)).length;

/** The row's name for assistive technology and messages: "Bia (thùng)" for an item in a unit. */
export const rowName = (r: Pick<PriceRow, 'name' | 'uomCode'>) => (r.uomCode ? `${r.name} (${r.uomCode})` : r.name);

/** Adds the chosen items in their units, or deals, not already in the table (a removed one comes back). */
export function addTargets(
  rows: readonly PriceRow[],
  kind: Kind,
  targets: readonly PriceTarget[],
  defaults: { price: string; from: string },
): PriceRow[] {
  const next = [...rows];
  for (const target of targets) {
    const uomId = target.unit?.uomId ?? null;
    const existing = next.findIndex((r) => r.targetId === target.id && (r.uomId ?? null) === uomId);
    if (existing >= 0) {
      const row = next[existing];
      if (row?.removed) next[existing] = { ...row, removed: false };
      continue;
    }
    next.push({
      key: nextKey(),
      kind,
      targetId: target.id,
      uomId,
      uomCode: target.unit?.code ?? null,
      uomName: target.unit?.name ?? null,
      units: target.units ?? [],
      // An item already in the table keeps one VAT category on the list: a new row takes it.
      taxCategoryId: next.find((r) => r.targetId === target.id && !r.removed)?.taxCategoryId ?? null,
      itemTaxCategoryId: next.find((r) => r.targetId === target.id)?.itemTaxCategoryId ?? null,
      code: target.code,
      name: target.name,
      groupName: target.groupName,
      isActive: target.isActive,
      price: defaults.price,
      from: defaults.from,
      to: '',
      removed: false,
    });
  }
  return next;
}

export function update(
  rows: readonly PriceRow[],
  key: string,
  change: Partial<Pick<PriceRow, 'price' | 'from' | 'to' | 'removed' | 'uomId' | 'taxCategoryId'>>,
) {
  // A new row that is removed simply goes away.
  if (change.removed && rows.find((r) => r.key === key && isNew(r))) return rows.filter((r) => r.key !== key);
  const row = rows.find((r) => r.key === key);
  return rows.map((r) => {
    if (r.key === key) {
      // Another of the item's units: its code and name come with it.
      const unit = change.uomId !== undefined ? r.units.find((u) => u.uomId === change.uomId) : undefined;
      return { ...r, ...change, ...(unit ? { uomCode: unit.code, uomName: unit.name } : {}) };
    }
    // The VAT category is the item's on this list: all its rows change together (ADR 0031).
    if (change.taxCategoryId !== undefined && row && r.targetId === row.targetId && r.kind === row.kind)
      return { ...r, taxCategoryId: change.taxCategoryId };
    return r;
  });
}

/** What stops saving, by row key. */
export function problems(rows: readonly PriceRow[]): Map<string, Problem> {
  const found = new Map<string, Problem>();
  const live = rows.filter((r) => !r.removed);
  const starts = new Map<string, number>();
  const startKey = (r: PriceRow) => `${unitKey(r.targetId, r.uomId)}|${r.from}`;
  for (const r of live) starts.set(startKey(r), (starts.get(startKey(r)) ?? 0) + 1);
  for (const r of live) {
    const price = Number(r.price);
    if (r.price.trim() === '' || !Number.isFinite(price) || price < 0) found.set(r.key, 'price');
    else if (!r.from) found.set(r.key, 'from');
    else if (r.to && r.to <= r.from) found.set(r.key, 'dates');
    else if ((starts.get(startKey(r)) ?? 0) > 1) found.set(r.key, 'duplicate');
  }
  return found;
}

export function toRequest(rows: readonly PriceRow[]) {
  return {
    rows: rows
      .filter((r) => !r.removed && (isNew(r) || isChanged(r)))
      .map((r) => ({
        entryId: r.entryId ?? null,
        version: r.version ?? null,
        kind: r.kind,
        targetId: r.targetId,
        uomId: r.kind === 'item' ? (r.uomId ?? null) : null,
        taxCategoryId: r.taxCategoryId,
        price: Number(r.price),
        effectiveFrom: fromDay(r.from),
        effectiveTo: r.to ? fromDay(r.to) : null,
      })),
    deleted: rows
      .filter((r) => r.removed && r.entryId)
      .map((r) => ({ entryId: r.entryId ?? '', version: r.version ?? 0 })),
  };
}
